import { differenceBy, intersectionBy, isArray, matchesProperty } from 'lodash-es';
import {
    BehaviorSubject,
    EMPTY,
    firstValueFrom,
    map,
    type Observable,
    of,
    Subject,
    switchMap,
    tap,
} from 'rxjs';
import type {
    DataType,
    DeleteMeasuringPointComment,
    MeasuringPointCommentType,
    UserType,
} from '@/graphql/types';
import { PERMISSION } from '../../../components/pages/sharing/utils';
import { NotImplementedError } from '../../../utils/errors';
import { getClient } from '../../../utils/apollo-client';
import type { DataSet } from '../../dataset';
import {
    Collection,
    createMutationObservable,
    shareReplayRefCount,
    withSideEffect,
} from '../../../utils/rxjs';
import { DELETE_COMMENT_MUTATION, UPDATE_MEASURING_POINT_COMMENT_MUTATION } from './queries';
import { fetchComment$, fetchComments$ } from './utils';

/* eslint @typescript-eslint/no-use-before-define: 0 */

export class Comment {
    collection: BaseCommentsCollection;

    id: string;

    dataType: DataType;

    measuringPointId: string;

    user: UserType;

    text$: BehaviorSubject<string>;

    start$: BehaviorSubject<number>;

    end$: BehaviorSubject<number>;

    createdAt$: BehaviorSubject<string>;

    editedAt$: BehaviorSubject<string | undefined>;

    editIsLoading$: BehaviorSubject<boolean>;

    editError$: BehaviorSubject<Error | undefined>;

    isEditing$: BehaviorSubject<boolean | undefined>;

    editText$: BehaviorSubject<string>;

    deleted$: Subject<void>;

    replyCollection: ReplyCollection;

    thread$: Observable<Comment[]>;

    constructor(collection: BaseCommentsCollection, id: string, data: MeasuringPointCommentType) {
        this.collection = collection;
        this.id = id;
        this.dataType = data.dataType!;
        this.measuringPointId = data.measuringPointId!;

        this.user = {} as UserType;
        this.text$ = new BehaviorSubject('');
        this.start$ = new BehaviorSubject(data.startTime as number);
        this.end$ = new BehaviorSubject(data.endTime as number);
        this.createdAt$ = new BehaviorSubject('');
        this.editedAt$ = new BehaviorSubject<string | undefined>(undefined);

        this.editIsLoading$ = new BehaviorSubject(false);
        this.editError$ = new BehaviorSubject<Error | undefined>(undefined);
        this.isEditing$ = new BehaviorSubject<boolean | undefined>(undefined);
        this.editText$ = new BehaviorSubject('');

        this.deleted$ = new Subject();

        // Sync updates to text onto the edit text.
        this.text$.subscribe(this.editText$);

        this.replyCollection = new ReplyCollection(this.collection.dataSet, this.id);

        // The thread observable is just all the replies plus the source comment prepended to it.
        this.thread$ = this.replyCollection.entries$.pipe(
            map((entries) => [this].concat(entries as (typeof this)[]))
        );

        this.syncWithApi(data);
    }

    setEditing(editing: boolean): void {
        this.isEditing$.next(editing);
    }

    setEditText(text: string): void {
        this.editText$.next(text);
    }

    syncWithApi(data: MeasuringPointCommentType): void {
        this.user = data.user!;
        this.text$.next(data.text);

        this.start$.next(data.startTime as number);
        this.end$.next(data.endTime as number);
        this.createdAt$.next(data.createdAt);
        this.editedAt$.next(data.editedAt as string);

        if (isArray(data.replies)) {
            this.replyCollection.syncWithApi(data.replies);
        }
    }

    setStart(start: number): void {
        this.start$.next(start);
    }

    setEnd(end: number): void {
        this.end$.next(end);
    }

    async delete(): Promise<void> {
        const success = await firstValueFrom(
            createMutationObservable<{
                deleteMeasuringPointComment: DeleteMeasuringPointComment;
            }>(getClient(), {
                variables: { id: this.id },
                mutation: DELETE_COMMENT_MUTATION,
                update(cache, { data }) {
                    cache.evict({
                        id: cache.identify(
                            data!.deleteMeasuringPointComment.measuringPointComment!
                        ),
                    });
                },
            })
        );

        if (success) {
            this.deleted$.next();
        }
    }

    async saveEdit(): Promise<void> {
        const editText = await firstValueFrom(this.editText$);

        const success = await firstValueFrom(
            createMutationObservable(
                getClient(),
                {
                    variables: {
                        id: this.id,
                        text: editText,
                    },
                    mutation: UPDATE_MEASURING_POINT_COMMENT_MUTATION,
                },
                this.editIsLoading$,
                this.editError$
            )
        );

        if (success) {
            this.setEditing(false);
        }
    }
}

class BaseCommentsCollection extends Collection<Comment> {
    isLoading$: BehaviorSubject<boolean>;

    error$: BehaviorSubject<Error | undefined>;

    dataSet: DataSet;

    constructor(dataSet: DataSet) {
        super();

        this.isLoading$ = new BehaviorSubject(false);
        this.error$ = new BehaviorSubject<Error | undefined>(undefined);

        this.dataSet = dataSet;
    }

    get prefetchReplies(): boolean {
        return this.dataSet.reportMode;
    }

    // We are intentionally widening the type here to
    // accept MeasuringPointCommentType directly.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    add(comment: MeasuringPointCommentType): Comment {
        return super.add(new Comment(this, comment.id, comment));
    }

    syncWithApi(data: MeasuringPointCommentType[]): void {
        // // Identify new comments from API.
        const newComments = differenceBy(data, this._entries$.getValue(), 'id');
        newComments.forEach((comment) => this.add(comment));

        // Identify comments to remove.
        const commentsToRemove = differenceBy(this._entries$.getValue(), data, 'id');
        commentsToRemove.forEach((comment) => this.delete(comment));

        // Identify comments to update.
        const commentsToUpdate = intersectionBy(data, this._entries$.getValue(), 'id');
        commentsToUpdate.forEach((comment) => {
            const existingComment = this._entries$
                .getValue()
                .find(matchesProperty('id', comment.id));
            existingComment?.syncWithApi(comment);
        });
    }

    get entries$(): Observable<Comment[]> {
        return this._entries$.pipe(withSideEffect(this.onSubscribe$()), shareReplayRefCount());
    }

    onSubscribe$(): Observable<MeasuringPointCommentType[] | void> {
        throw new NotImplementedError();
    }
}

class ReplyCollection extends BaseCommentsCollection {
    id: string;

    constructor(dataSet: DataSet, parentId: string) {
        super(dataSet);

        this.id = parentId;
    }

    onSubscribe$() {
        if (this.prefetchReplies) {
            // We don't have to fetch the comments of measuring point comments
            // if the measuring point comments already prefetched those. Prefetching
            // happens in reports.
            return EMPTY;
        }

        return fetchComment$(this.id, this.isLoading$, this.error$).pipe(
            map((data) => (data?.replies || []) as MeasuringPointCommentType[]),
            tap((data) => {
                // Sync incoming data with collection.
                this.syncWithApi(data);
            })
        );
    }
}

export class MeasuringPointCommentsCollection extends BaseCommentsCollection {
    constructor(dataSet: DataSet) {
        super(dataSet);

        this.isLoading$
            .pipe(
                switchMap((loading) => (loading ? this.dataSet.createLoadingSideEffect$() : EMPTY))
            )
            .subscribe();
    }

    onSubscribe$() {
        return this.dataSet.matchesPermissions$([PERMISSION.FullAccess, PERMISSION.View]).pipe(
            switchMap((canView) => {
                // If the user does not have at least view access, leave this collection
                // empty and do not fetch the comments.
                if (!canView) {
                    return of([]);
                }

                // Fetch the comments.
                // TODO: Add retry mechanism.
                return this.dataSet.sensor$.pipe(
                    switchMap((id) =>
                        id
                            ? fetchComments$(id, this.prefetchReplies, this.isLoading$, this.error$)
                            : of([])
                    ),
                    map((data) => data || [])
                );
            }),
            tap((data) => {
                // Sync incoming data with collection.
                this.syncWithApi(data);
            })
        );
    }
}
